import React, { useEffect, useRef, useState } from 'react';
import { CREATE_AGENT_PROMPT_RECOMMENDATIONS } from '@/data/createAgentPromptRecommendations';

/**
 * Horizontal auto-scrolling prompt recommender.
 * Click fills the intake text only — user confirms with Create.
 */
export function CreateAgentPromptRecommender({
  onSelect,
  className = '',
  'data-testid': testId = 'create-agent-prompt-recommender',
}) {
  const trackRef = useRef(null);
  const viewportRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [offset, setOffset] = useState(0);
  const [openIndex, setOpenIndex] = useState(null);
  const items = CREATE_AGENT_PROMPT_RECOMMENDATIONS;
  const loopItems = [...items, ...items];
  let firstName = '';
  try {
    const user = JSON.parse(localStorage.getItem('storycraft_user') || '{}');
    firstName = String(user.name || user.full_name || '').trim().split(/\s+/)[0] || '';
  } catch {
    firstName = '';
  }

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const speedPxPerSec = 42;

    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!paused) {
        setOffset((prev) => {
          const track = trackRef.current;
          const half = track ? track.scrollWidth / 2 : 0;
          if (half <= 0) return prev + speedPxPerSec * dt;
          let next = prev + speedPxPerSec * dt;
          if (next >= half) next -= half;
          return next;
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport) return;
    if (openIndex == null || !track) {
      viewport.style.height = '';
      return;
    }
    const card = track.querySelector('[data-expanded="true"]');
    if (!card) return;
    const styles = window.getComputedStyle(track);
    const pad = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    viewport.style.height = `${Math.ceil(card.offsetHeight + pad)}px`;
  }, [openIndex]);

  return (
    <aside
      className={`flex flex-col ${className}`}
      data-testid={testId}
      aria-label="Create agent capability prompts"
    >
      <div className="mb-2 shrink-0 min-w-0 [container-type:inline-size]">
        <p className="whitespace-nowrap font-mono font-extrabold italic tracking-tight text-white leading-none text-[clamp(13px,3.15cqi,30px)]">
          {firstName ? `Hey ${firstName}! ` : 'Hey! '}Let's Create your next video
        </p>
      </div>
      <div
        ref={viewportRef}
        className={`relative overflow-hidden rounded-xl border border-white/10 bg-black/35 ${openIndex == null ? 'h-[88px]' : ''}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
        }}
        data-testid={`${testId}-viewport`}
      >
        <ul
          ref={trackRef}
          className="flex w-max items-start gap-2 p-1.5 will-change-transform"
          role="list"
          style={{ transform: `translateX(-${offset}px)` }}
        >
          {loopItems.map((row, idx) => (
            <li key={`${row.id}-${idx}`} className="w-[240px] shrink-0">
              <button
                type="button"
                onClick={() => onSelect?.(row)}
                onMouseEnter={() => setOpenIndex(idx)}
                onMouseLeave={() => setOpenIndex(null)}
                onFocus={() => setOpenIndex(idx)}
                onBlur={() => setOpenIndex(null)}
                data-expanded={openIndex === idx ? 'true' : undefined}
                className={`w-full text-left rounded-lg border px-2.5 py-2 transition-colors cursor-pointer overflow-hidden ${
                  openIndex === idx
                    ? 'h-auto border-[#2DD4BF]/35 bg-[#12161e]'
                    : 'h-[76px] border-white/5 bg-white/[0.03] hover:bg-white/[0.08] hover:border-[#2DD4BF]/35'
                }`}
                data-testid={`create-agent-prompt-${row.id}`}
              >
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md border border-[#2DD4BF]/25 text-[#5EEAD4] bg-[#2DD4BF]/10">
                    {row.flowLabel}
                  </span>
                  <span className={`text-[9px] text-[#9CA3AF] ${openIndex === idx ? '' : 'truncate'}`}>{row.capability}</span>
                </div>
                <p className={`text-[11px] sm:text-[12px] leading-snug ${openIndex === idx ? 'text-white' : 'text-[#E5E7EB] line-clamp-2'}`}>
                  {row.prompt}
                </p>
              </button>
            </li>
          ))}
        </ul>
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-6"
          style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.55), transparent)' }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-8"
          style={{ background: 'linear-gradient(to left, rgba(0,0,0,0.55), transparent)' }}
        />
      </div>
    </aside>
  );
}

export default CreateAgentPromptRecommender;
